import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { formatCurrency, formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';
import { showToast } from '@/lib/custom-toast';
import {
  CreditCard, CheckCircle2, AlertCircle, MessageSquare,
  ArrowLeft, User, Building2, Wallet, Sparkles, Send, ThumbsUp, ThumbsDown,
  Calculator, Zap, Paperclip, Download, X, Check, ShieldCheck, Percent, CheckCheck,
  Copy, Plus, ListChecks, Clock, Image as ImageIcon, ExternalLink
} from 'lucide-react';

const CREDITOR_LABELS = { person: 'Persona', bank: 'Banco', card: 'Tarjeta', service: 'Servicio', other: 'Otro' };
const FREQUENCY_LABELS = { none: 'Sin plan fijo', weekly: 'Semanal', biweekly: 'Quincenal', monthly: 'Mensual' };
const STATUS_CONFIG = {
  active: { label: 'En progreso', color: 'bg-amber-100 text-amber-700' },
  paid:   { label: 'Liquidada ✓', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Cancelada', color: 'bg-slate-100 text-slate-500' },
};

const todayISO = () => new Date().toISOString().split('T')[0];

// Compresor de imagen ultraligero usando Canvas HTML5
const compressReceipt = (file, maxWidth = 1200, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

// Hook para arrastre táctil diferenciado en modales bottom-sheet
const useBottomSheetDrag = (onClose) => {
  const [dragCurrentY, setDragCurrentY] = useState(0);
  const [dragStartY, setDragStartY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const isFromHandle = useRef(false);

  const handleTouchStart = (e, fromHandle = false) => {
    isFromHandle.current = fromHandle;
    setDragStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const diff = e.touches[0].clientY - dragStartY;
    if (diff > 0) {
      setDragCurrentY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    const threshold = isFromHandle.current ? 75 : 190;
    if (dragCurrentY > threshold) {
      onClose();
    }
    setDragCurrentY(0);
    isFromHandle.current = false;
  };

  return {
    dragStyle: {
      transform: isDragging && dragCurrentY > 0 ? `translateY(${dragCurrentY}px)` : undefined,
      transition: isDragging ? 'none' : 'transform 0.2s ease-out'
    },
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  };
};

const ModalDragHandle = ({ onTouchStart, onTouchMove, onTouchEnd }) => (
  <div
    onTouchStart={(e) => onTouchStart(e, true)}
    onTouchMove={onTouchMove}
    onTouchEnd={onTouchEnd}
    className="sm:hidden pt-3 pb-1 cursor-grab active:cursor-grabbing flex justify-center shrink-0 select-none bg-inherit"
  >
    <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
  </div>
);

// Modal Registrar Pago / Abono con Comprobante (Para el Deudor)
const RegisterPublicPaymentModal = ({ debt, shareToken, onClose, onSaved }) => {
  const { dragStyle, handleTouchStart, handleTouchMove, handleTouchEnd } = useBottomSheetDrag(onClose);
  const [kind, setKind] = useState('regular');
  const defaultAmount = debt?.installment_amount ? String(debt.installment_amount) : (debt?.current_balance ? String(debt.current_balance) : '');
  const [amount, setAmount] = useState(defaultAmount);
  const [description, setDescription] = useState(debt?.payment_frequency !== 'none' ? `Pago cuota ${FREQUENCY_LABELS[debt?.payment_frequency] || ''}` : 'Pago / Abono');
  const [date, setDate] = useState(todayISO());
  const [payerName, setPayerName] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [compressing, setCompressing] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  const handleKindChange = (newKind) => {
    setKind(newKind);
    if (newKind === 'regular') {
      setDescription(`Pago cuota ${FREQUENCY_LABELS[debt?.payment_frequency] || 'regular'}`);
    } else {
      setDescription('⚡ Abono extraordinario');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      showToast.error('El comprobante no debe superar 10 MB');
      return;
    }
    try {
      setCompressing(true);
      const compressed = await compressReceipt(file);
      setReceiptUrl(compressed);
      showToast.success('Comprobante preparado ✓');
    } catch {
      showToast.error('Error al procesar la imagen');
    } finally {
      setCompressing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (!num || num <= 0) {
      showToast.error('Ingresa un monto válido mayor a $0');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/public/debt/${shareToken}/payment`, {
        amount: num,
        description,
        date,
        payer_name: payerName.trim() || 'Deudor',
        notes: notes.trim() || null,
        receipt_url: receiptUrl,
        is_extra: kind === 'extra'
      });
      showToast.success('¡Pago registrado con éxito! El acreedor ha sido notificado.');
      onSaved();
      onClose();
    } catch (err) {
      showToast.error(err?.response?.data?.detail || 'Error al registrar el pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={dragStyle}
        onTouchStart={(e) => handleTouchStart(e, false)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="w-full sm:max-w-md bg-white rounded-t-[2.5rem] sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col border border-slate-100"
      >
        <ModalDragHandle onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} />
        <div className="flex items-center justify-between px-6 pt-3 sm:pt-5 pb-3 border-b border-slate-100 bg-white">
          <div>
            <h3 className="font-black text-slate-900 text-lg">Registrar Pago</h3>
            <p className="text-xs text-slate-400">{debt?.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-900 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto">
          {/* Tipo de movimiento: Solo Pago Normal y Abono Extra */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">Tipo de Movimiento</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleKindChange('regular')}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
                  kind === 'regular'
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/25 scale-[1.02]'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Pago Normal</span>
              </button>
              <button
                type="button"
                onClick={() => handleKindChange('extra')}
                className={`py-2.5 px-3 rounded-2xl text-xs font-bold transition-all flex flex-col items-center gap-1 ${
                  kind === 'extra'
                    ? 'bg-amber-500 text-white shadow-md shadow-amber-500/25 scale-[1.02]'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                <Zap className="w-4 h-4" />
                <span>Abono Extra</span>
              </button>
            </div>
          </div>

          {/* Monto */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Monto Total del Pago *</label>
              {debt?.installment_amount && kind === 'regular' && (
                <span className="text-[10px] text-indigo-600 font-bold">Cuota pactada: {formatCurrency(debt.installment_amount)}</span>
              )}
            </div>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={amount ? formatCurrencyInput(amount) : ''}
                onChange={e => setAmount(parseCurrency(e.target.value))}
                className="w-full pl-8 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/30 font-black text-slate-900 text-lg"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* Concepto */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Concepto</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-sm font-medium"
              placeholder="Ej: Pago quincenal..."
              required
            />
          </div>

          {/* Fecha y Nombre */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Fecha</label>
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-xs font-medium"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Tu Nombre</label>
              <input
                type="text"
                value={payerName}
                onChange={e => setPayerName(e.target.value)}
                placeholder="Ej: Salma"
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none text-xs font-medium"
              />
            </div>
          </div>

          {/* Adjuntar Comprobante */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Paperclip className="w-3.5 h-3.5 text-emerald-500" />
              Comprobante de Transferencia
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            {receiptUrl ? (
              <div className="relative rounded-2xl border border-emerald-200 p-2.5 bg-emerald-50/50 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <img src={receiptUrl} alt="Preview" className="w-12 h-12 rounded-xl object-cover border" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-emerald-950 truncate">Comprobante listo ✓</p>
                    <p className="text-[10px] text-emerald-600 font-medium">Se enviará al acreedor</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReceiptUrl(null)}
                  className="w-7 h-7 rounded-full bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 transition-all shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={compressing}
                className="w-full py-3 px-4 border border-dashed border-slate-300 rounded-2xl flex items-center justify-center gap-2 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-all"
              >
                <ImageIcon className="w-4 h-4 text-emerald-500" />
                {compressing ? 'Comprimiendo...' : 'Subir Foto de Comprobante'}
              </button>
            )}
          </div>

          {/* Notas */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Notas (opcional)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none resize-none text-xs"
              rows={2}
              placeholder="Folio de transferencia o detalle..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold rounded-2xl shadow-lg shadow-emerald-600/25 active:scale-95 transition-all disabled:opacity-60 text-sm"
          >
            {loading ? 'Registrando...' : 'Confirmar y Enviar Pago'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Modal Visor de Comprobante
const ReceiptModal = ({ receiptUrl, onClose }) => {
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  if (!receiptUrl) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex flex-col items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-white/10 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-slate-950">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-emerald-400" />
            <h3 className="text-white font-black text-sm">Comprobante Adjunto</h3>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={receiptUrl}
              download="comprobante.jpg"
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all"
            >
              <Download className="w-4 h-4" />
            </a>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-3 flex items-center justify-center bg-black/40">
          <img src={receiptUrl} alt="Comprobante" className="max-w-full max-h-[70vh] object-contain rounded-xl shadow-lg" />
        </div>
      </div>
    </div>
  );
};

// Modal de Validación de Movimiento (Abonos o Aumentos/Préstamos)
const VerifyMovementModal = ({ movement, onClose, onVerified, shareToken }) => {
  const { dragStyle, handleTouchStart, handleTouchMove, handleTouchEnd } = useBottomSheetDrag(onClose);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const isPayment = movement.type === 'payment';

  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/public/debt/${shareToken}/verify-movement/${movement.id}`, {
        verifier_name: name.trim()
      });
      onVerified();
      onClose();
    } catch {
      alert('Error al validar movimiento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={dragStyle}
        onTouchStart={(e) => handleTouchStart(e, false)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 pt-3 sm:pt-6 shadow-2xl space-y-4"
      >
        <ModalDragHandle onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} />
        <div className="flex items-center justify-between border-b pb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <h3 className="font-bold text-slate-900 text-base">
              {isPayment ? 'Validar Recepción de Pago' : 'Validar Aumento / Préstamo'}
            </h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-100 text-xs text-slate-600 space-y-1">
          <p><span className="font-bold">Monto:</span> {formatCurrency(movement.amount)}</p>
          <p><span className="font-bold">Concepto:</span> {movement.description}</p>
          <p><span className="font-bold">Fecha:</span> {movement.date?.split('T')[0]}</p>
        </div>
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Tu Nombre Completo *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ fontSize: '16px' }}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/30 font-medium"
              placeholder="Ej: Mamá, Juan Pérez..."
              required
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all disabled:opacity-60 text-sm"
          >
            {loading ? 'Confirmando...' : (isPayment ? '✓ Confirmar que recibí este pago' : '✓ Confirmar de conformidad este aumento')}
          </button>
        </form>
      </div>
    </div>
  );
};

// Modal de Validación del Plan de Pagos
const VerifyPaymentPlanModal = ({ debt, onClose, onVerified, shareToken }) => {
  const { dragStyle, handleTouchStart, handleTouchMove, handleTouchEnd } = useBottomSheetDrag(onClose);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/public/debt/${shareToken}/verify-payment-plan`, {
        verifier_name: name.trim()
      });
      onVerified();
      onClose();
    } catch {
      alert('Error al validar el plan de pagos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={dragStyle}
        onTouchStart={(e) => handleTouchStart(e, false)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 pt-3 sm:pt-6 shadow-2xl space-y-4"
      >
        <ModalDragHandle onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} />
        <div className="flex items-center justify-between border-b pb-3">
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-indigo-600" />
            <h3 className="font-bold text-slate-900 text-base">Validar Plan de Pagos</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="bg-indigo-50/70 p-3.5 rounded-2xl border border-indigo-100 text-xs text-indigo-950 space-y-1">
          <p><span className="font-bold">Frecuencia:</span> {FREQUENCY_LABELS[debt.payment_frequency] || debt.payment_frequency}</p>
          {debt.installment_amount && <p><span className="font-bold">Cuota acordada:</span> {formatCurrency(debt.installment_amount)}</p>}
          {debt.total_installments && <p><span className="font-bold">Total cuotas:</span> {debt.total_installments}</p>}
        </div>
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Tu Nombre Completo *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ fontSize: '16px' }}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 font-medium"
              placeholder="Ej: Mamá, Juan Pérez..."
              required
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all disabled:opacity-60 text-sm"
          >
            {loading ? 'Confirmando...' : '✓ Confirmar Conformidad con el Plan'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Modal de Resolver Aclaración / Inconformidad en Comentarios
const ResolveCommentModal = ({ comment, onClose, onResolved, shareToken }) => {
  const { dragStyle, handleTouchStart, handleTouchMove, handleTouchEnd } = useBottomSheetDrag(onClose);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  const handleResolve = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/public/debt/${shareToken}/resolve-comment/${comment.id}`, {
        resolver_name: name.trim()
      });
      onResolved();
      onClose();
    } catch {
      alert('Error al resolver aclaración');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={dragStyle}
        onTouchStart={(e) => handleTouchStart(e, false)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 pt-3 sm:pt-6 shadow-2xl space-y-4"
      >
        <ModalDragHandle onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} />
        <div className="flex items-center justify-between border-b pb-3">
          <div className="flex items-center gap-2">
            <CheckCheck className="w-5 h-5 text-emerald-600" />
            <h3 className="font-bold text-slate-900 text-base">Resolver Aclaración</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="bg-amber-50 p-3.5 rounded-2xl border border-amber-200 text-xs text-amber-900 space-y-1">
          <p className="font-bold">{comment.author_name} reportó:</p>
          <p className="italic">"{comment.content}"</p>
        </div>
        <form onSubmit={handleResolve} className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">¿Quién confirma la resolución? *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ fontSize: '16px' }}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500/30 font-medium"
              placeholder="Ej: Mamá, Gaby, Juan..."
              required
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all disabled:opacity-60 text-sm"
          >
            {loading ? 'Guardando...' : '✓ Confirmar que quedó Resuelto'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Modal de Conformidad / Inconformidad Global
const AgreementModal = ({ type, onClose, onSubmitted, shareToken }) => {
  const { dragStyle, handleTouchStart, handleTouchMove, handleTouchEnd } = useBottomSheetDrag(onClose);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (type === 'inconforme' && !notes.trim()) {
      alert('Por favor describe el motivo de la inconformidad o aclaración.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/public/debt/${shareToken}/agreement`, {
        status: type,
        author_name: name.trim(),
        notes: notes.trim()
      });
      onSubmitted();
      onClose();
    } catch {
      alert('Error al registrar respuesta');
    } finally {
      setLoading(false);
    }
  };

  const isConforme = type === 'conforme';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={dragStyle}
        onTouchStart={(e) => handleTouchStart(e, false)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 pt-3 sm:pt-6 shadow-2xl space-y-4"
      >
        <ModalDragHandle onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} />
        <div className="flex items-center justify-between border-b pb-3">
          <div className="flex items-center gap-2">
            {isConforme ? <ThumbsUp className="w-5 h-5 text-emerald-600" /> : <ThumbsDown className="w-5 h-5 text-amber-600" />}
            <h3 className="font-bold text-slate-900 text-base">
              {isConforme ? 'Confirmar de Conformidad' : 'Reportar Inconformidad / Aclaración'}
            </h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-slate-500">
          {isConforme
            ? 'Al confirmar, quedará registrado que estás 100% de acuerdo con el saldo actual, conceptos y abonos registrados.'
            : 'Indica qué datos no coinciden para que el titular pueda revisarlo y corregirlo.'}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Tu Nombre Completo *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={{ fontSize: '16px' }}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 font-medium"
              placeholder="Ej: Mamá, Juan Pérez..."
              required
              autoFocus
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              {isConforme ? 'Comentario adicional (opcional)' : 'Detalle de la aclaración *'}
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ fontSize: '16px' }}
              className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none resize-none"
              rows={3}
              placeholder={isConforme ? 'Todo en orden...' : 'Ej: Falta agregar el pago de ayer por $1,000 en efectivo...'}
              required={!isConforme}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3.5 text-white font-bold rounded-2xl shadow-lg active:scale-95 transition-all disabled:opacity-60 text-sm ${
              isConforme
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 shadow-emerald-500/25'
                : 'bg-gradient-to-r from-amber-500 to-orange-600 shadow-amber-500/25'
            }`}
          >
            {loading ? 'Enviando...' : (isConforme ? '✓ Firmar de Conformidad' : '⚠️ Enviar Reporte de Inconformidad')}
          </button>
        </form>
      </div>
    </div>
  );
};

const PublicDebt = () => {
  const { shareToken, token: altToken } = useParams();
  const token = shareToken || altToken;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const [verifyingMovement, setVerifyingMovement] = useState(null);
  const [resolvingComment, setResolvingComment] = useState(null);
  const [verifyingPlan, setVerifyingPlan] = useState(false);
  const [agreementType, setAgreementType] = useState(null); // 'conforme' | 'inconforme'
  const [activeTab, setActiveTab] = useState('payments'); // 'payments' | 'breakdown'
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [form, setForm] = useState({ author_name: '', author_email: '', content: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { fetchDebt(); }, [token]);

  const fetchDebt = async () => {
    try {
      const res = await axios.get(`${API}/public/debt/${token}`);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Link no válido o deuda no encontrada');
    } finally {
      setLoading(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!form.author_name.trim() || !form.content.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/debt/${token}/comment`, { ...form, is_agreement: false });
      setSubmitted(true);
      setForm({ author_name: '', author_email: '', content: '' });
      fetchDebt();
    } catch { /* silencioso */ }
    finally { setSubmitting(false); }
  };

  const { user, token: authToken } = useContext(AuthContext);

  const handleClaimOrNavigate = async () => {
    if (authToken) {
      try {
        await axios.post(`${API}/shared-expenses/claim/${token}`, {}, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        showToast.success('Deuda vinculada a tu cuenta');
      } catch (err) {
        // Ya vinculada o creador original
      }
      navigate('/debts');
    } else {
      localStorage.setItem('pending_debt_claim_token', token);
      navigate(`/login?claim_token=${token}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 text-center">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-10 h-10 text-slate-400" />
        </div>
        <h1 className="text-xl font-black text-slate-800 mb-2">Link no disponible</h1>
        <p className="text-slate-500 mb-6">{error}</p>
        <button onClick={() => navigate('/')} className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-2xl">
          Ir a Portal de Gastos
        </button>
      </div>
    );
  }

  const {
    name, creditor_type, initial_amount, total_amount, current_balance, currency, description,
    color, payment_frequency, installment_amount, total_installments, start_date,
    status, agreement_status, agreement_author, agreement_date,
    payment_plan_verified_by, payment_plan_verified_at,
    has_interest, interest_rate, accumulated_interest, total_interest_paid,
    items = [], movements = [], comments = []
  } = data || {};
  const totalCharges = (movements || []).filter(m => m.type === 'charge').reduce((s, m) => s + m.amount, 0);
  const totalPaid = (movements || []).filter(m => m.type === 'payment').reduce((s, m) => s + m.amount, 0);
  // total_amount ya incluye los cargos en el backend (initial_amount + total_charges)
  const effectiveTotal = initial_amount !== undefined 
    ? (Number(initial_amount) + totalCharges) 
    : (Number(total_amount) || 0);
  const progress = effectiveTotal > 0 ? Math.min(Math.max((totalPaid / effectiveTotal) * 100, 0), 100) : 0;
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  const totalExtra = (movements || []).filter(m => m.is_extra).reduce((s, m) => s + m.amount, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="relative px-4 pb-6" style={{ background: `linear-gradient(135deg,${color}dd,${color}88)`, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)' }}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-white" />
            </div>
            <span className="text-white/80 text-xs font-bold tracking-wide">Portal de Gastos</span>
          </div>
          <span className={`text-[10px] px-2.5 py-1 rounded-full font-bold ${statusCfg.color}`}>{statusCfg.label}</span>
        </div>

        <h1 className="text-2xl font-black text-white mb-1">{name}</h1>
        <p className="text-white/70 text-sm font-medium mb-1">{CREDITOR_LABELS[creditor_type] || 'Deuda'}</p>
        {description && <p className="text-white/60 text-xs mb-4">{description}</p>}

        {/* Resumen */}
        <div className="bg-white/15 backdrop-blur rounded-2xl p-4">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center">
              <p className="text-white/60 text-[10px] font-bold uppercase">Total Deuda</p>
              <p className="text-white font-black text-base">{formatCurrency(effectiveTotal)}</p>
              <p className="text-white/60 text-[9px]">{currency}</p>
            </div>
            <div className="text-center border-x border-white/20">
              <p className="text-white/60 text-[10px] font-bold uppercase">Pagado</p>
              <p className="text-emerald-300 font-black text-base">{formatCurrency(totalPaid)}</p>
              {totalExtra > 0 && <p className="text-amber-200 text-[9px]">({formatCurrency(totalExtra)} extra)</p>}
            </div>
            <div className="text-center">
              <p className="text-white/60 text-[10px] font-bold uppercase">
                {has_interest ? 'Para Liquidar' : 'Resta'}
              </p>
              <p className="text-white font-black text-base">{formatCurrency(current_balance)}</p>
              <p className="text-white/60 text-[9px]">{currency}</p>
            </div>
          </div>

          <div className="h-2 bg-white/20 rounded-full overflow-hidden mb-1">
            <div className="h-full bg-white rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-white/70 text-[10px] text-right font-bold">{progress.toFixed(0)}% liquidado</p>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-5 space-y-4">
        {/* Banner para Vincular Deuda a la Cuenta de PGastos */}
        <div className="bg-gradient-to-r from-indigo-900 to-slate-900 text-white p-5 rounded-3xl shadow-xl flex flex-col sm:flex-row items-center justify-between gap-4 border border-indigo-700/40 animate-fade-in">
          <div className="space-y-1 text-center sm:text-left">
            <p className="font-black text-sm flex items-center justify-center sm:justify-start gap-1.5 text-indigo-300">
              <Sparkles className="w-4 h-4 text-indigo-400" />
              ¿Quieres dar seguimiento a este plan desde tu cuenta?
            </p>
            <p className="text-xs text-slate-300">
              Inicia sesión o regístrate para vincular esta deuda a tu cuenta con recordatorios y sincronización automática.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClaimOrNavigate}
            className="px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold rounded-xl shadow-md transition-all active:scale-95 shrink-0"
          >
            {authToken ? 'Ver en Mi Cuenta' : 'Guardar en Mi Cuenta'}
          </button>
        </div>

        {/* BANNER DE CONFORMIDAD INICIAL O REPORTE */}
        {agreement_status ? (
          <div className={`p-4 rounded-2xl border flex items-center justify-between gap-3 ${
            agreement_status === 'conforme'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-amber-50 border-amber-200 text-amber-900'
          }`}>
            <div className="flex items-center gap-3">
              {agreement_status === 'conforme'
                ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                : <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              }
              <div>
                <p className="font-bold text-xs">
                  {agreement_status === 'conforme' ? '✓ Conformidad Confirmada' : '⚠️ Aclaración / Inconformidad Reportada'}
                </p>
                <p className="text-[11px] opacity-75">
                  Por {agreement_author || 'Acreedor'} • {agreement_date?.split('T')[0]}
                </p>
              </div>
            </div>
            <button
              onClick={() => setAgreementType(agreement_status === 'conforme' ? 'inconforme' : 'conforme')}
              className="text-[11px] font-bold underline shrink-0 hover:opacity-80"
            >
              Cambiar
            </button>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-3xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-600" />
              <h3 className="font-black text-indigo-950 text-xs uppercase tracking-wider">¿Estás de acuerdo con estos datos?</h3>
            </div>
            <p className="text-xs text-indigo-900 leading-relaxed">
              Puedes confirmar tu conformidad o reportar cualquier discrepancia para que el titular lo ajuste.
            </p>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                onClick={() => setAgreementType('conforme')}
                className="py-2.5 px-3 bg-emerald-600 text-white rounded-2xl font-bold text-xs shadow-sm hover:bg-emerald-700 active:scale-95 transition-all flex items-center justify-center gap-1.5"
              >
                <ThumbsUp className="w-3.5 h-3.5" /> De Acuerdo
              </button>
              <button
                onClick={() => setAgreementType('inconforme')}
                className="py-2.5 px-3 bg-white text-amber-700 border border-amber-200 rounded-2xl font-bold text-xs hover:bg-amber-50 active:scale-95 transition-all flex items-center justify-center gap-1.5"
              >
                <ThumbsDown className="w-3.5 h-3.5" /> Aclaración
              </button>
            </div>
          </div>
        )}

        {/* Tarjeta de Intereses sobre Saldo Insoluto */}
        {has_interest && (
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl p-5 text-white shadow-lg shadow-amber-500/20 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center">
                  <Percent className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-xs font-black uppercase tracking-wider text-amber-100">Interés Mensual</h3>
                  <p className="text-sm font-bold">{interest_rate}% mensual sobre saldo</p>
                </div>
              </div>
              <span className="text-xs px-2.5 py-1 bg-white/20 rounded-full font-bold">Activo</span>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1 border-t border-white/20 text-xs">
              <div className="bg-black/10 rounded-xl p-2.5">
                <p className="text-amber-100 text-[10px] font-bold">Interés acumulado pendiente</p>
                <p className="font-black text-base mt-0.5">{formatCurrency(accumulated_interest || 0)}</p>
              </div>
              <div className="bg-black/10 rounded-xl p-2.5">
                <p className="text-amber-100 text-[10px] font-bold">Total intereses pagados</p>
                <p className="font-black text-base mt-0.5">{formatCurrency(total_interest_paid || 0)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Plan de Pagos */}
        {payment_frequency && payment_frequency !== 'none' && (
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-indigo-600">
                <Calculator className="w-4 h-4" />
                <h2 className="font-black text-slate-800 text-sm">Plan de Pagos Acordado</h2>
              </div>
              {payment_plan_verified_by ? (
                <span className="text-[10px] px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-bold flex items-center gap-1">
                  ✓ Plan Validado por {payment_plan_verified_by}
                </span>
              ) : (
                <button
                  onClick={() => setVerifyingPlan(true)}
                  className="text-[11px] px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-sm flex items-center gap-1 active:scale-95"
                >
                  ✓ Validar Plan de Pagos
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1 text-xs">
              <div>
                <p className="text-slate-400 font-bold uppercase text-[10px]">Frecuencia</p>
                <p className="font-bold text-slate-800 mt-0.5">{FREQUENCY_LABELS[payment_frequency]}</p>
              </div>
              {installment_amount && (
                <div>
                  <p className="text-slate-400 font-bold uppercase text-[10px]">Cuota Acordada</p>
                  <p className="font-bold text-slate-800 mt-0.5">{formatCurrency(installment_amount)}</p>
                </div>
              )}
              {total_installments && (
                <div>
                  <p className="text-slate-400 font-bold uppercase text-[10px]">Total Cuotas</p>
                  <p className="font-bold text-slate-800 mt-0.5">{total_installments} pagos</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Datos Bancarios para Transferir (SPEI) - Solo cuando al creador le deben */}
        {data.direction !== 'i_owe' && data.bank_details && (data.bank_details.clabe || data.bank_details.holder_name) && (
          <div className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                Datos para Transferir
              </span>
              <span className="text-[11px] text-slate-400 font-medium">SPEI / Interbancario</span>
            </div>

            <div className="bg-slate-50 rounded-2xl border border-slate-100 divide-y divide-slate-200/60 overflow-hidden text-xs">
              {data.bank_details.holder_name && (
                <div className="p-3.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-400 font-bold uppercase">Titular de la cuenta</p>
                    <p className="font-bold text-slate-900 truncate mt-0.5">{data.bank_details.holder_name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(data.bank_details.holder_name);
                      showToast.success('Nombre del titular copiado');
                    }}
                    className="p-2 bg-white hover:bg-slate-100 text-indigo-600 rounded-xl transition-colors shrink-0 shadow-xs border border-slate-200"
                    title="Copiar Titular"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {data.bank_details.bank_name && (
                <div className="p-3.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-400 font-bold uppercase">Banco Destino</p>
                    <p className="font-bold text-slate-900 truncate mt-0.5">{data.bank_details.bank_name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(data.bank_details.bank_name);
                      showToast.success('Banco copiado');
                    }}
                    className="p-2 bg-white hover:bg-slate-100 text-indigo-600 rounded-xl transition-colors shrink-0 shadow-xs border border-slate-200"
                    title="Copiar Banco"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {data.bank_details.clabe && (
                <div className="p-3.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-400 font-bold uppercase">CLABE Interbancaria</p>
                    <p className="font-mono font-bold text-emerald-600 text-sm tracking-wide truncate mt-0.5">
                      {data.bank_details.clabe}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(data.bank_details.clabe);
                      showToast.success('CLABE copiada al portapapeles');
                    }}
                    className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl transition-colors shrink-0 shadow-xs border border-emerald-200"
                    title="Copiar CLABE"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TABS IDÉNTICOS A DEUDAS: Historial de Pagos vs Desglose de Conceptos */}
        <div className="flex bg-slate-200/80 p-1.5 rounded-2xl">
          <button
            type="button"
            onClick={() => setActiveTab('payments')}
            className={`flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
              activeTab === 'payments' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Historial de Pagos ({movements.length})</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('breakdown')}
            className={`flex-1 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
              activeTab === 'breakdown' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
            }`}
          >
            <ListChecks className="w-3.5 h-3.5" />
            <span>Desglose de Conceptos ({items.length || 1})</span>
          </button>
        </div>

        {/* TAB 1: HISTORIAL DE PAGOS CON BOTÓN + REGISTRAR PAGO */}
        {activeTab === 'payments' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-black text-slate-800 text-sm">Movimientos y Abonos</h2>
                <p className="text-[11px] text-slate-400">Registros y pagos aplicados a la deuda</p>
              </div>
              {data?.direction !== 'i_owe' && (
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(true)}
                  className="flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-xl shadow-md shadow-emerald-600/20 active:scale-95 transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Registrar Pago
                </button>
              )}
            </div>

            {movements.length === 0 ? (
              <div className="text-center py-10 bg-white rounded-3xl border border-slate-100 p-6 space-y-2">
                <CreditCard className="w-10 h-10 text-slate-300 mx-auto" />
                <p className="text-slate-700 font-bold text-sm">Sin pagos registrados aún</p>
                <p className="text-slate-400 text-xs max-w-xs mx-auto">
                  {data?.direction === 'i_owe'
                    ? 'Aún no se han registrado abonos en este plan de pagos.'
                    : '¿Realizaste una transferencia o abono? Puedes registrarlo aquí con tu foto de comprobante.'}
                </p>
                {data?.direction !== 'i_owe' && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => setShowPaymentModal(true)}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md transition-all active:scale-95"
                    >
                      + Registrar Primer Pago
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {movements.map(m => (
                  <div key={m.id} className="bg-white border border-slate-100 p-3.5 rounded-2xl flex items-center justify-between gap-3 shadow-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                        m.is_extra
                          ? 'bg-amber-50'
                          : (m.type === 'payment' ? 'bg-emerald-50' : 'bg-rose-50')
                      }`}>
                        {m.is_extra ? (
                          <Zap className="w-5 h-5 text-amber-500" />
                        ) : (
                          m.type === 'payment' ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <AlertCircle className="w-5 h-5 text-rose-500" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-bold text-sm text-slate-900 truncate">{m.description}</p>
                          {m.is_extra && <span className="text-[9px] px-1.5 py-0.2 bg-amber-100 text-amber-700 rounded-full font-bold">⚡ Extra</span>}
                          {m.receipt_url && (
                            <button
                              type="button"
                              onClick={() => setViewingReceipt(m.receipt_url)}
                              className="text-[9px] px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full font-bold flex items-center gap-1 hover:bg-indigo-100 transition-all shrink-0"
                            >
                              <Paperclip className="w-2.5 h-2.5" />
                              Comprobante
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <p className="text-[11px] text-slate-400">{m.date?.split('T')[0]}</p>
                          {m.verified_by ? (
                            <span className="text-[10px] px-2 py-0.2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-bold flex items-center gap-1">
                              ✓ Validado por {m.verified_by}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setVerifyingMovement(m)}
                              className="text-[10px] px-2.5 py-0.5 bg-slate-100 hover:bg-emerald-100 text-slate-700 hover:text-emerald-800 rounded-full font-bold transition-all border border-slate-200"
                            >
                              {m.type === 'payment' ? '✓ Validar recepción' : '✓ Validar aumento'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className={`font-black text-sm shrink-0 ${m.type === 'payment' ? (m.is_extra ? 'text-amber-600' : 'text-emerald-600') : 'text-rose-600'}`}>
                      {m.type === 'payment' ? '-' : '+'}{formatCurrency(m.amount)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 2: DESGLOSE DE CONCEPTOS (Llenado automático si no hay items) */}
        {activeTab === 'breakdown' && (
          <div className="space-y-3">
            <div>
              <h2 className="font-black text-slate-800 text-sm">Conceptos de esta Deuda</h2>
              <p className="text-[11px] text-slate-400">
                {items.length > 0
                  ? `Desglosado: ${formatCurrency(items.reduce((s, it) => s + (Number(it.amount) || 0), 0))} de ${formatCurrency(effectiveTotal)}`
                  : `Concepto acordado: ${formatCurrency(effectiveTotal)}`}
              </p>
            </div>

            {items.length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-black text-sm text-slate-900 truncate">
                        {name || 'Concepto acordado'}
                      </p>
                      {current_balance <= 0 ? (
                        <span className="text-[10px] px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-bold">
                          ✓ Liquidado
                        </span>
                      ) : (effectiveTotal - current_balance > 0) ? (
                        <span className="text-[10px] px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-full font-bold">
                          {Math.min(100, Math.round(((effectiveTotal - current_balance) / (effectiveTotal || 1)) * 100))}% cubierto
                        </span>
                      ) : (
                        <span className="text-[10px] px-2.5 py-0.5 bg-slate-100 text-slate-500 rounded-full font-medium">
                          Pendiente
                        </span>
                      )}
                      <span className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full font-medium">
                        Concepto asignado
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500">
                      {description || 'Concepto fijado por quien solicita el pago'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-black text-sm text-slate-900">{formatCurrency(effectiveTotal)}</p>
                    {effectiveTotal - current_balance > 0 && (
                      <p className="text-[10px] text-blue-600 font-bold">
                        Abonado: {formatCurrency(Math.max(0, effectiveTotal - current_balance))}
                      </p>
                    )}
                  </div>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${current_balance <= 0 ? 'bg-emerald-500' : 'bg-indigo-600'}`}
                    style={{ width: `${Math.min(100, Math.round(((effectiveTotal - current_balance) / (effectiveTotal || 1)) * 100))}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map(item => {
                  const itemAmt = Number(item.amount) || 0;
                  return (
                    <div key={item.id} className="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm space-y-2">
                      <div className="flex items-start sm:items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-sm text-slate-900">{item.title}</p>
                          {item.notes && <p className="text-[10px] text-slate-400 mt-0.5">{item.notes}</p>}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-black text-sm text-slate-900">{formatCurrency(itemAmt)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Comentarios y Aclaraciones */}
        {comments.length > 0 && (
          <div>
            <h2 className="font-black text-slate-800 text-base mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-indigo-500" /> Comentarios y Acuerdos
            </h2>
            <div className="space-y-2.5">
              {comments.map(c => (
                <div key={c.id} className={`p-4 rounded-2xl ${c.is_agreement ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-white border border-slate-100 shadow-sm'}`}>
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <p className="font-bold text-sm text-slate-800">{c.author_name}</p>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {c.is_agreement && <span className="text-[10px] px-2 py-0.5 bg-emerald-500 text-white rounded-full font-bold">✓ De acuerdo</span>}
                      {c.comment_type === 'inconforme' && (
                        c.is_resolved ? (
                          <span className="text-[10px] px-2 py-0.5 bg-emerald-600 text-white rounded-full font-bold">
                            ✓ Resuelto por {c.resolved_by || 'Acreedor'}
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 bg-amber-500 text-white rounded-full font-bold">
                            ⚠️ Aclaración
                          </span>
                        )
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">{c.content}</p>
                  
                  {c.is_resolved && c.resolved_by_movement_description && (
                    <div className="mt-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-medium">
                      ✓ {c.resolved_by_movement_description} ({c.resolved_at?.split('T')[0]})
                    </div>
                  )}

                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                    <p className="text-[10px] text-slate-400">{c.created_at?.split('T')[0]}</p>
                    {c.comment_type === 'inconforme' && !c.is_resolved && (
                      <button
                        onClick={() => setResolvingComment(c)}
                        className="text-[11px] px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-bold shadow-sm active:scale-95 transition-all flex items-center gap-1"
                      >
                        <CheckCheck className="w-3.5 h-3.5" /> Marcar como Resuelto
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Formulario de comentario */}
        <div>
          <h2 className="font-black text-slate-800 text-base mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-slate-400" /> Dejar un comentario
          </h2>
          {submitted ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="font-bold text-emerald-700">¡Comentario registrado y notificado al titular!</p>
              <button onClick={() => setSubmitted(false)} className="mt-3 text-xs text-emerald-600 font-bold">Escribir otro</button>
            </div>
          ) : (
            <form onSubmit={handleComment} className="bg-white border border-slate-100 rounded-2xl p-4 space-y-3 shadow-sm">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Tu nombre *</label>
                <input
                  type="text"
                  value={form.author_name}
                  onChange={e => setForm(f => ({ ...f, author_name: e.target.value }))}
                  style={{ fontSize: '16px' }}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 font-medium"
                  placeholder="Nombre completo"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Correo (opcional)</label>
                <input
                  type="email"
                  value={form.author_email}
                  onChange={e => setForm(f => ({ ...f, author_email: e.target.value }))}
                  style={{ fontSize: '16px' }}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none font-medium"
                  placeholder="tu@correo.com"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Mensaje *</label>
                <textarea
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  style={{ fontSize: '16px' }}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 resize-none font-medium"
                  rows={3}
                  placeholder="Escribe tu mensaje o confirmación de pago..."
                  required
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl shadow-lg active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2 text-sm"
              >
                <Send className="w-4 h-4" />
                {submitting ? 'Enviando...' : 'Enviar Comentario'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Modales */}
      {showPaymentModal && (
        <RegisterPublicPaymentModal
          debt={data}
          shareToken={token}
          onClose={() => setShowPaymentModal(false)}
          onSaved={fetchDebt}
        />
      )}
      {viewingReceipt && <ReceiptModal receiptUrl={viewingReceipt} onClose={() => setViewingReceipt(null)} />}
      {verifyingMovement && (
        <VerifyMovementModal
          movement={verifyingMovement}
          shareToken={token}
          onClose={() => setVerifyingMovement(null)}
          onVerified={fetchDebt}
        />
      )}
      {verifyingPlan && (
        <VerifyPaymentPlanModal
          debt={data}
          shareToken={token}
          onClose={() => setVerifyingPlan(false)}
          onVerified={fetchDebt}
        />
      )}
      {resolvingComment && (
        <ResolveCommentModal
          comment={resolvingComment}
          shareToken={token}
          onClose={() => setResolvingComment(null)}
          onResolved={fetchDebt}
        />
      )}
      {agreementType && (
        <AgreementModal
          type={agreementType}
          shareToken={token}
          onClose={() => setAgreementType(null)}
          onSubmitted={fetchDebt}
        />
      )}
    </div>
  );
};

export default PublicDebt;
